import type { Id } from '../core/model';
import type { StudioApi } from '../engine/api';
import type { RemoteCommand } from './api';

export type RemoteHandler = (payload: unknown) => unknown | Promise<unknown>;

const COMMAND_TYPE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const MAX_VOLUME = 4;

function payloadObject(payload: unknown, type: string): Record<string, unknown> {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    throw new Error(`"${type}" expects an object payload`);
  }
  return payload as Record<string, unknown>;
}

function requireString(p: Record<string, unknown>, key: string, type: string): string {
  const v = p[key];
  if (typeof v !== 'string' || v === '') throw new Error(`"${type}" requires a string "${key}"`);
  return v;
}

function requireBoolean(p: Record<string, unknown>, key: string, type: string): boolean {
  const v = p[key];
  if (typeof v !== 'boolean') throw new Error(`"${type}" requires a boolean "${key}"`);
  return v;
}

export function validateCommand(command: unknown): RemoteCommand {
  if (typeof command !== 'object' || command === null || Array.isArray(command)) {
    throw new Error('A remote command must be an object');
  }
  const { type, payload } = command as { type?: unknown; payload?: unknown };
  if (typeof type !== 'string' || !COMMAND_TYPE_PATTERN.test(type)) {
    throw new Error('A remote command needs a "type" of letters, digits, ".", "_" or "-"');
  }
  return { type, payload };
}

export function createBuiltinHandlers(studio: StudioApi): Record<string, RemoteHandler> {
  const project = () => studio.project.getState().project;
  const requireScene = (id: Id) => {
    if (!project().scenes[id]) throw new Error(`Unknown scene "${id}"`);
  };
  const requireSource = (id: Id) => {
    if (!project().sources[id]) throw new Error(`Unknown source "${id}"`);
  };

  const sceneList = () => {
    const p = project();
    const current = studio.runtime.getState().programSceneId ?? p.currentSceneId;
    return p.sceneOrder.map((id) => ({ id, name: p.scenes[id]!.name, current: id === current }));
  };

  return {
    'scene.list': () => sceneList(),

    'scene.select': (payload) => {
      const sceneId = requireString(payloadObject(payload, 'scene.select'), 'sceneId', 'scene.select');
      requireScene(sceneId);
      studio.selectScene(sceneId);
      return null;
    },

    'transition.run': async (payload) => {
      let sceneId: string | undefined;
      if (payload !== undefined && payload !== null) {
        const p = payloadObject(payload, 'transition.run');
        if (p.sceneId !== undefined) {
          sceneId = requireString(p, 'sceneId', 'transition.run');
          requireScene(sceneId);
        }
      }
      await studio.transition(sceneId);
      return null;
    },

    'recording.start': async () => {
      await studio.startRecording();
      return null;
    },
    'recording.stop': async () => await studio.stopRecording(),
    'broadcast.start': async () => {
      await studio.startBroadcast();
      return null;
    },
    'broadcast.stop': async () => {
      await studio.stopBroadcast();
      return null;
    },

    'source.setMuted': (payload) => {
      const p = payloadObject(payload, 'source.setMuted');
      const sourceId = requireString(p, 'sourceId', 'source.setMuted');
      const muted = requireBoolean(p, 'muted', 'source.setMuted');
      requireSource(sourceId);
      studio.commands.setAudioStrip(sourceId, { muted });
      return null;
    },

    'source.setVolume': (payload) => {
      const p = payloadObject(payload, 'source.setVolume');
      const sourceId = requireString(p, 'sourceId', 'source.setVolume');
      const volume = p.volume;
      if (typeof volume !== 'number' || !Number.isFinite(volume) || volume < 0 || volume > MAX_VOLUME) {
        throw new Error(`"source.setVolume" requires a linear "volume" between 0 and ${MAX_VOLUME}`);
      }
      requireSource(sourceId);
      studio.commands.setAudioStrip(sourceId, { volume });
      return null;
    },

    'item.setVisible': (payload) => {
      const p = payloadObject(payload, 'item.setVisible');
      const sceneId = requireString(p, 'sceneId', 'item.setVisible');
      const itemId = requireString(p, 'itemId', 'item.setVisible');
      const visible = requireBoolean(p, 'visible', 'item.setVisible');
      requireScene(sceneId);
      if (!studio.queries.findItem(project(), sceneId, itemId)) throw new Error(`Unknown item "${itemId}" in scene "${sceneId}"`);
      studio.commands.updateItem(sceneId, itemId, { visible });
      return null;
    },

    'state.get': () => {
      const p = project();
      const rt = studio.runtime.getState();
      return {
        collection: { id: p.id, name: p.name },
        programSceneId: rt.programSceneId,
        previewSceneId: rt.previewSceneId,
        studioMode: rt.studioMode,
        transition: { active: rt.transition.active, progress: rt.transition.progress },
        scenes: sceneList(),
        sources: Object.values(p.sources).map((s) => ({
          id: s.id,
          name: s.name,
          kind: s.kind,
          muted: s.audio.muted,
          volume: s.audio.volume,
        })),
        recording: {
          status: rt.recording.status,
          durationMs: rt.recording.durationMs,
          bytesWritten: rt.recording.bytesWritten,
          error: rt.recording.error,
        },
        broadcast: { status: rt.broadcast.status, peers: rt.broadcast.peers.length, error: rt.broadcast.error },
      };
    },
  };
}
